<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import UserMenu from './UserMenu.svelte';
  import ChatListItem from './ChatListItem.svelte';
  import ConfirmModal from '../common/ConfirmModal.svelte';
  import { getPalette } from '../../theme.svelte';
  import { navigate, getRoute } from '../../route.svelte';
  import {
    type ChatsResponse,
    type SearchChatResult,
    groups as groupsState,
    setGroupsFromBackend,
    markChatRead,
    pinChat,
    renameChat,
    deleteChat,
    searchChats,
    requestScrollToMessage,
  } from '../../chats.svelte';
  import { currentUser, initializeAuth } from '../../auth.svelte';
  import { showGlobalError, showGlobalSuccess } from '../../errorBanner.svelte';
  import { LIST_CHATS_ENDPOINT, MAIN_URL } from '../../externalUrls'

  let {
    isCollapsed = false,
    palette = getPalette(),
    isMobile = false,
    onCloseSidebar
  } = $props();

  let isLoading = $state(true);
  let error = $state<string | null>(null);

  let route = $derived(getRoute());
  let activeChatId = $derived(route.name === 'chat' ? route.id : null);
  let isBibliotecaRoute = $derived(
    route.name === 'biblioteca' || route.name === 'biblioteca-document'
  );
  let isSeguimientoRoute = $derived(
    route.name === 'seguimiento' || route.name === 'seguimiento-detalle'
  );

  // ── Confirm delete state ──
  let confirmDelete = $state({ open: false, chatId: '' });

  // ── Global search (lupa de chats) ──
  const MIN_SEARCH_LENGTH = 2;
  const SEARCH_DEBOUNCE_MS = 300;

  let searchQuery = $state('');
  let searchResults = $state<SearchChatResult[]>([]);
  let searchTotalMatches = $state(0);
  let searchLoading = $state(false);
  let searchError = $state<string | null>(null);
  let searchInputEl: HTMLInputElement | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let searchRequestId = 0;

  let trimmedQuery = $derived(searchQuery.trim());
  let isSearching = $derived(trimmedQuery.length >= MIN_SEARCH_LENGTH);

  function handleSearchInput(event: Event) {
    searchQuery = (event.currentTarget as HTMLInputElement).value;
    scheduleSearch();
  }

  function scheduleSearch() {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }

    // Invalidar la petición anterior al escribir, no 300 ms después.
    const requestId = ++searchRequestId;
    const query = searchQuery.trim();
    searchResults = [];
    searchTotalMatches = 0;
    if (query.length < MIN_SEARCH_LENGTH) {
      searchLoading = false;
      searchError = null;
      return;
    }

    searchLoading = true;
    searchError = null;
    searchTimer = setTimeout(() => {
      searchTimer = null;
      void runSearch(query, requestId);
    }, SEARCH_DEBOUNCE_MS);
  }

  async function runSearch(query: string, requestId: number) {
    try {
      const data = await searchChats(query);
      if (requestId !== searchRequestId) return; // respuesta obsoleta
      searchResults = data.chats;
      searchTotalMatches = data.totalMatches;
      searchError = null;
    } catch (e) {
      if (requestId !== searchRequestId) return;
      console.error(e);
      searchResults = [];
      searchTotalMatches = 0;
      searchError = 'No se pudo buscar. Intenta de nuevo.';
    } finally {
      if (requestId === searchRequestId) searchLoading = false;
    }
  }

  function clearSearch() {
    if (searchTimer) {
      clearTimeout(searchTimer);
      searchTimer = null;
    }
    searchRequestId++;
    searchQuery = '';
    searchResults = [];
    searchTotalMatches = 0;
    searchLoading = false;
    searchError = null;
    searchInputEl?.focus();
  }

  function handleSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      clearSearch();
    }
  }

  function openSearchResult(chatId: string, messageId: number) {
    // Registra el mensaje objetivo: ChatView hace scroll y lo resalta al
    // terminar de cargar el historial (o al instante si ya está abierto).
    requestScrollToMessage(chatId, messageId);
    openChat(chatId);
  }

  function openSearchChat(result: SearchChatResult) {
    const firstMatch = result.matches[0];
    if (firstMatch) {
      openSearchResult(result.chatId, firstMatch.messageId);
    } else {
      openChat(result.chatId);
    }
  }

  type HighlightPart = { text: string; hit: boolean };

  const COMBINING_MARK_RE = /\p{M}/u;

  /** Elimina diacríticos ('revisión' → 'revision'), igual que unaccent en el back. */
  function foldText(text: string): string {
    let folded = '';
    for (const char of text.normalize('NFKD')) {
      if (!COMBINING_MARK_RE.test(char)) folded += char;
    }
    return folded.toLowerCase();
  }

  /** Texto plegado + mapa posición plegada → posición original. */
  function foldWithMap(text: string): { folded: string; map: number[] } {
    const foldedChars: string[] = [];
    const map: number[] = [];
    for (let index = 0; index < text.length; index++) {
      let stripped = '';
      for (const char of text[index].normalize('NFKD')) {
        if (!COMBINING_MARK_RE.test(char)) stripped += char;
      }
      for (const char of stripped) {
        foldedChars.push(char);
        map.push(index);
      }
    }
    return { folded: foldedChars.join('').toLowerCase(), map };
  }

  /**
   * Divide el snippet en tramos para resaltar el término buscado (sin HTML).
   * La coincidencia ignora mayúsculas y acentos; el texto mostrado conserva
   * sus acentos originales.
   */
  function highlightParts(text: string, query: string): HighlightPart[] {
    const needle = foldText(query.trim());
    if (!needle || !text) return [{ text, hit: false }];

    const { folded, map } = foldWithMap(text);
    const parts: HighlightPart[] = [];
    let foldedCursor = 0;
    let originalCursor = 0;

    while (foldedCursor < folded.length) {
      const found = folded.indexOf(needle, foldedCursor);
      if (found === -1) {
        parts.push({ text: text.slice(originalCursor), hit: false });
        break;
      }

      const startOriginal = map[found];
      const endOriginal = map[found + needle.length - 1] + 1;
      foldedCursor = found + needle.length;

      // Solape teórico (ligaduras que pliegan a varios caracteres): ignorar.
      if (endOriginal <= originalCursor) continue;
      if (startOriginal > originalCursor) {
        parts.push({ text: text.slice(originalCursor, startOriginal), hit: false });
      }
      parts.push({
        text: text.slice(Math.max(startOriginal, originalCursor), endOriginal),
        hit: true
      });
      originalCursor = endOriginal;
    }

    return parts;
  }

  function roleLabel(role: string): string {
    if (role === 'user') return 'Tú';
    if (role === 'system') return 'Sistema';
    return 'IA';
  }

  onDestroy(() => {
    if (searchTimer) clearTimeout(searchTimer);
    searchRequestId++;
  });

  async function fetchChats() {
    try {
      isLoading = true;
      error = null;
      const res = await fetch(LIST_CHATS_ENDPOINT, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Error ${res.status}`);
      }
      const data = (await res.json()) as ChatsResponse;
      setGroupsFromBackend(data.chats ?? []);
    } catch (e) {
      console.error(e);
      error = 'No se pudieron cargar los chats.';
    } finally {
      isLoading = false;
    }
  }

  function handleBackdropClick() {
    if (isMobile && !isCollapsed && onCloseSidebar) {
      onCloseSidebar();
    }
  }

  function openChat(id: string) {
    markChatRead(id);
    navigate(`/chat/${id}`);
    if (isMobile && onCloseSidebar) {
      onCloseSidebar();
    }
  }

  function openBiblioteca() {
    navigate('/biblioteca');
    if (isMobile && onCloseSidebar) {
      onCloseSidebar();
    }
  }

  function openSeguimiento() {
    navigate('/seguimiento-judicial');
    if (isMobile && onCloseSidebar) {
      onCloseSidebar();
    }
  }

  // ── Chat actions ──
  async function handlePin(chatId: string, pinned: boolean) {
    try {
      await pinChat(chatId, pinned);
    } catch (e: any) {
      showGlobalError(e?.message || 'Error al fijar chat');
    }
  }

  async function handleRename(chatId: string, newTitle: string) {
    try {
      await renameChat(chatId, newTitle);
    } catch (e: any) {
      showGlobalError(e?.message || 'Error al renombrar chat');
    }
  }

  function handleDeleteRequest(chatId: string) {
    confirmDelete = { open: true, chatId };
  }

  async function handleDeleteConfirm() {
    const chatId = confirmDelete.chatId;
    confirmDelete = { open: false, chatId: '' };
    try {
      await deleteChat(chatId);
      if (activeChatId === chatId) {
        navigate('/');
      }
      showGlobalSuccess('Chat eliminado exitosamente');
    } catch (e: any) {
      showGlobalError(e?.message || 'Error al eliminar chat');
    }
  }

  onMount(() => {
    void initializeAuth().then((authenticated) => {
      if (authenticated) return fetchChats();
      isLoading = false;
    });
  });
</script>

<!-- ── Confirm delete modal ── -->
<ConfirmModal
  open={confirmDelete.open}
  title="Eliminar chat"
  message="¿Estás seguro de que deseas eliminar este chat? Esta acción no se puede deshacer."
  confirmLabel="Eliminar"
  cancelLabel="Cancelar"
  {palette}
  isDanger={true}
  onConfirm={handleDeleteConfirm}
  onCancel={() => (confirmDelete = { open: false, chatId: '' })}
/>

<!-- ── Chat list snippet (reused in both branches) ── -->
{#snippet chatList()}
  {#if isLoading}
    <div class="status-text">Cargando chats...</div>
  {:else if error}
    <div class="status-text error">{error}</div>
  {:else if groupsState.groups.length === 0}
    <div class="status-text">No hay chats aún.</div>
  {:else}
    {#each groupsState.groups as group}
      <div class="group">
        <div class="recent-title" style="color: {palette.textSecondary};">
          {group.name}
        </div>
        {#each group.chats as chat}
          <ChatListItem
            {chat}
            isActive={chat.id === activeChatId}
            {isMobile}
            {palette}
            onOpen={openChat}
            onPin={handlePin}
            onRename={handleRename}
            onDelete={handleDeleteRequest}
          />
        {/each}
      </div>
    {/each}
  {/if}
{/snippet}

<!-- ── Búsqueda global: input con lupa ── -->
{#snippet searchBox()}
  <div
    class="search-box"
    style={`background-color: ${palette.bgChatInput}; border-color: ${palette.borderSoft};`}
  >
    <i class="bi bi-search search-icon" aria-hidden="true" style={`color: ${palette.icon};`}></i>
    <input
      bind:this={searchInputEl}
      value={searchQuery}
      oninput={handleSearchInput}
      onkeydown={handleSearchKeydown}
      type="search"
      placeholder="Buscar en tus chats"
      aria-label="Buscar en tus chats"
      autocomplete="off"
      spellcheck="false"
    />
    {#if searchLoading}
      <span class="search-spinner" aria-hidden="true"></span>
    {:else if searchQuery}
      <button
        class="search-clear bi bi-x-lg"
        type="button"
        aria-label="Limpiar búsqueda"
        title="Limpiar búsqueda"
        onclick={clearSearch}
      ></button>
    {/if}
  </div>
{/snippet}

<!-- ── Búsqueda global: resultados agrupados por chat ── -->
{#snippet searchResultsPanel()}
  {#if searchError}
    <div class="status-text error">{searchError}</div>
  {:else if searchLoading && searchResults.length === 0}
    <div class="status-text">Buscando…</div>
  {:else if searchResults.length === 0}
    <div class="status-text">Sin resultados para “{trimmedQuery}”.</div>
  {:else}
    <div class="search-summary" style={`color: ${palette.textSecondary};`}>
      {searchTotalMatches}
      {searchTotalMatches === 1 ? 'coincidencia' : 'coincidencias'}
      en {searchResults.length}
      {searchResults.length === 1 ? 'chat' : 'chats'}
    </div>

    {#each searchResults as result (result.chatId)}
      <div class="search-chat">
        <button
          class="search-chat-title"
          type="button"
          title={result.title}
          onclick={() => openSearchChat(result)}
        >
          <i class="bi bi-chat-left-text" aria-hidden="true"></i>
          <span>{result.title}</span>
          {#if result.titleMatch}
            <i class="bi bi-type search-title-badge" title="Coincide con el título" aria-hidden="true"></i>
          {/if}
        </button>

        {#if result.matches.length === 0 && result.titleMatch}
          <div class="search-title-hit" style={`color: ${palette.textSecondary};`}>
            Coincide con el título del chat
          </div>
        {/if}

        {#each result.matches as match (match.messageId)}
          <button
            class="search-match"
            type="button"
            onclick={() => openSearchResult(result.chatId, match.messageId)}
          >
            <span class="search-match-role" style={`color: ${palette.textSecondary};`}>
              {roleLabel(match.role)}
            </span>
            <span class="search-match-text">
              {#if match.snippet}
                {#each highlightParts(match.snippet, trimmedQuery) as part}
                  {#if part.hit}<mark class="search-hit">{part.text}</mark>{:else}{part.text}{/if}
                {/each}
              {:else}
                <span class="search-empty-snippet" style={`color: ${palette.textSecondary};`}>
                  Contenido sin texto
                </span>
              {/if}
            </span>
          </button>
        {/each}

        {#if result.matchCount > result.matches.length}
          <div class="search-more" style={`color: ${palette.textSecondary};`}>
            +{result.matchCount - result.matches.length} coincidencias más en este chat
          </div>
        {/if}
      </div>
    {/each}
  {/if}
{/snippet}

<!-- ── Lista de chats o resultados de búsqueda ── -->
{#snippet chatListOrSearch()}
  {#if isSearching}
    {@render searchResultsPanel()}
  {:else}
    {@render chatList()}
  {/if}
{/snippet}

{#snippet bibliotecaNavigation()}
  <button
    class:active={isBibliotecaRoute}
    class="biblioteca-link"
    type="button"
    aria-current={isBibliotecaRoute ? 'page' : undefined}
    onclick={openBiblioteca}
    style={`color: ${isBibliotecaRoute ? palette.accent : palette.textPrimary};`}
  >
    <i class="bi bi-journal-text" aria-hidden="true"></i>
    <span>Biblioteca jurídica</span>
  </button>
{/snippet}

{#snippet seguimientoNavigation()}
  <button
    class:active={isSeguimientoRoute}
    class="seguimiento-link"
    type="button"
    aria-current={isSeguimientoRoute ? 'page' : undefined}
    onclick={openSeguimiento}
    style={`color: ${isSeguimientoRoute ? palette.accent : palette.textPrimary};`}
  >
    <i class="bi bi-clipboard2-pulse" aria-hidden="true"></i>
    <span>Seguimiento judicial</span>
  </button>
{/snippet}

<!-- ── MOBILE ── -->
{#if isMobile}
  {#if !isCollapsed}
    <button
      class="sidebar-backdrop"
      type="button"
      aria-label="Cerrar menú lateral"
      onclick={handleBackdropClick}
    ></button>
  {/if}
  <div
    class="sidebar-overlay {isCollapsed ? 'hidden' : 'visible'}"
    style={`background-color: ${palette.bgSidebar}; color: ${palette.textPrimary}; border-right: 1px solid ${palette.sidebarDivider};`}
  >
    <div class="top scroll-y">
      <div class="logo-row">
        <a class="logo-link" href={MAIN_URL} aria-label="Ir a Jurismart">
          <img class="logo" src={palette.sidebarLogoSrc} alt="Jurismart logo" />
        </a>
        <button
          class="new-chat bi bi-plus-lg"
          type="button"
          title="Nuevo chat"
          onclick={() => navigate('/')}
          style={`color: ${palette.icon}; border-color: ${palette.borderSoft};`}
        ></button>
      </div>

      {@render searchBox()}
      {@render bibliotecaNavigation()}
      {@render seguimientoNavigation()}
      {@render chatListOrSearch()}
    </div>

    <div class="spacer"></div>
    <UserMenu
      username={currentUser.username ? currentUser.username : 'Usuario'}
      avatarSrc={currentUser.src ? currentUser.src : undefined}
      {palette}
    />
  </div>
{:else}
  <!-- ── DESKTOP ── -->
  <div class="sidebar-wrapper {isCollapsed ? 'collapsed' : ''}">
    <div
      class="sidebar-content {isCollapsed ? 'sidebar-hidden' : 'sidebar-visible'}"
      style={`background-color: ${palette.bgSidebar}; color: ${palette.textPrimary}; border-right: 1px solid ${palette.sidebarDivider};`}
    >
      <div class="top scroll-y">
        <div class="logo-row">
          <a class="logo-link" href={MAIN_URL} aria-label="Ir a Jurismart">
            <img class="logo" src={palette.sidebarLogoSrc} alt="Jurismart logo" />
          </a>
          <button
            class="new-chat bi bi-plus-lg"
            type="button"
            title="Nuevo chat"
            onclick={() => navigate('/')}
            style={`color: ${palette.icon}; border-color: ${palette.borderSoft};`}
          ></button>
        </div>

        {@render searchBox()}
        {@render bibliotecaNavigation()}
        {@render seguimientoNavigation()}
        {@render chatListOrSearch()}
      </div>

      <div class="spacer"></div>
      <UserMenu
        username={currentUser.username ? currentUser.username : 'Usuario'}
        avatarSrc={currentUser.src ? currentUser.src : undefined}
        {palette}
      />
    </div>
  </div>
{/if}

<style>
  /* -------- DESKTOP -------- */

  .sidebar-wrapper {
    width: 260px;
    min-width: 260px;
    transition: width 0.18s ease;
    overflow: hidden;
  }

  .sidebar-wrapper.collapsed {
    width: 0;
    min-width: 0;
  }

  .sidebar-content {
    display: flex;
    flex-direction: column;
    width: 260px;
    min-width: 260px;
    height: 100%;
    transition:
      transform 0.18s ease,
      opacity 0.18s ease;
  }

  .sidebar-visible {
    transform: translateX(0);
    opacity: 1;
  }

  .sidebar-hidden {
    transform: translateX(-20px);
    opacity: 0;
  }

  .top {
    padding: 12px;
    max-height: calc(100% - 80px);
    overflow-y: auto;
  }

  .logo-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  .logo {
    height: 30px;
    object-fit: contain;
  }

  .logo-link {
    display: inline-flex;
    align-items: center;
  }

  .new-chat {
    border: 1px solid transparent;
    border-radius: 16px;
    width: 26px;
    height: 26px;
    background: transparent;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .new-chat:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .biblioteca-link,
  .seguimiento-link {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 9px;
    margin: 0 0 8px;
    padding: 9px 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }

  .seguimiento-link {
    margin-bottom: 16px;
  }

  .biblioteca-link:hover,
  .biblioteca-link:focus-visible,
  .seguimiento-link:hover,
  .seguimiento-link:focus-visible {
    border-color: currentColor;
    background: color-mix(in srgb, currentColor 10%, transparent);
    outline: none;
  }

  .biblioteca-link.active,
  .seguimiento-link.active {
    border-color: currentColor;
    background: color-mix(in srgb, currentColor 14%, transparent);
  }

  .biblioteca-link i,
  .seguimiento-link i {
    font-size: 15px;
  }

  .group + .group {
    margin-top: 16px;
  }

  .recent-title {
    font-size: 12px;
    opacity: 0.8;
    margin-bottom: 6px;
  }

  .spacer {
    flex: 1;
  }

  .status-text {
    font-size: 12px;
    opacity: 0.8;
  }

  .status-text.error {
    color: #e57373;
  }

  /* -------- Búsqueda global -------- */

  .search-box {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 14px;
    padding: 7px 10px;
    border: 1px solid;
    border-radius: 10px;
    transition: border-color 0.15s ease;
  }

  .search-box:focus-within {
    border-color: currentColor;
  }

  .search-icon {
    flex: 0 0 auto;
    font-size: 13px;
    opacity: 0.75;
  }

  .search-box input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 13px;
    outline: none;
  }

  .search-box input::placeholder {
    color: currentColor;
    opacity: 0.55;
  }

  /* Ocultamos la X nativa: usamos nuestro propio botón. */
  .search-box input::-webkit-search-cancel-button,
  .search-box input::-webkit-search-decoration {
    -webkit-appearance: none;
    display: none;
  }

  .search-clear {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: inherit;
    opacity: 0.7;
    cursor: pointer;
    padding: 0 2px;
    font-size: 11px;
    line-height: 1;
  }

  .search-clear:hover {
    opacity: 1;
  }

  .search-spinner {
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    opacity: 0.7;
    animation: search-spin 0.7s linear infinite;
  }

  @keyframes search-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .search-summary {
    font-size: 11px;
    margin: 0 2px 10px;
  }

  .search-chat {
    margin-bottom: 12px;
  }

  .search-chat-title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    text-align: left;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
  }

  .search-chat-title:hover,
  .search-chat-title:focus-visible {
    background: color-mix(in srgb, currentColor 10%, transparent);
    outline: none;
  }

  .search-chat-title span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .search-title-badge {
    flex: 0 0 auto;
    font-size: 10px;
    opacity: 0.65;
  }

  .search-title-hit {
    font-size: 11px;
    padding: 0 6px 4px;
  }

  .search-match {
    display: flex;
    gap: 7px;
    width: 100%;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    padding: 5px 6px;
    border-radius: 6px;
    cursor: pointer;
  }

  .search-match:hover,
  .search-match:focus-visible {
    background: color-mix(in srgb, currentColor 10%, transparent);
    outline: none;
  }

  .search-match-role {
    flex: 0 0 auto;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding-top: 2px;
  }

  .search-match-text {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    line-height: 1.45;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .search-hit {
    background: color-mix(in srgb, #f0b429 55%, transparent);
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
  }

  .search-empty-snippet {
    font-style: italic;
  }

  .search-more {
    font-size: 10.5px;
    padding: 2px 6px;
  }

  /* -------- MÓVIL: overlay -------- */

  .sidebar-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 20;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(80%, 280px);
    display: flex;
    flex-direction: column;
    padding-top: 8px;
    z-index: 30;
    transition:
      transform 0.18s ease,
      opacity 0.18s ease;
    transform: translateX(-100%);
    opacity: 0;
  }

  .sidebar-overlay.visible {
    transform: translateX(0);
    opacity: 1;
  }

  .sidebar-overlay.hidden {
    transform: translateX(-100%);
    opacity: 0;
    pointer-events: none;
  }

  .sidebar-overlay .top {
    max-height: calc(100% - 80px);
    overflow-y: auto;
  }

  /* scrollbar discreto opcional */
  .top::-webkit-scrollbar {
    width: 6px;
  }

  .top::-webkit-scrollbar-track {
    background: transparent;
  }

  .top::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.18);
    border-radius: 3px;
  }
</style>
